import React, { useCallback, useEffect, useState } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import {
  UserPlus,
  Link2,
  Copy,
  CheckCircle,
  AlertCircle,
  BarChart3,
  Users,
  Pencil,
  X,
} from 'lucide-react';
import { buildInfluencerTrackingUrl } from '../../utils/influencerAttribution';
import {
  INFLUENCER_PIX_KEY_TYPES,
  InfluencerPixKeyType,
} from '../../types/influencer';

export interface InfluencerRecord {
  id: string;
  name: string;
  cpf: string;
  email: string;
  phone: string;
  instagram: string;
  pixKeyType: InfluencerPixKeyType | '';
  pixKey: string;
  commissionPercent: number;
  code: string;
  active: boolean;
  createdAt?: Date;
}

export interface InfluencerSaleRecord {
  id: string;
  influencerId: string;
  influencerCode: string;
  influencerName: string;
  buyerName: string;
  buyerEmail: string;
  planName: string;
  value: number;
  paymentType: string;
  status: string;
  commissionPercent?: number;
  commissionValue?: number;
  payoutStatus?: string;
  payoutError?: string;
  createdAt?: Date;
}

function slugifyName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 36);
}

function generateInfluencerCode(name: string): string {
  const base = slugifyName(name) || 'influencer';
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${base}-${suffix}`;
}

function maskCpf(cpf: string): string {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return '***.***.***-**';
  return `***.***.${digits.slice(6, 9)}-**`;
}

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function paymentTypeLabel(type: string): string {
  if (type === 'subscription_first') return 'Assinatura (1ª)';
  if (type === 'subscription_renewal') return 'Assinatura (renovação)';
  return 'Avulso';
}

function payoutStatusLabel(status: string): string {
  switch (status) {
    case 'COMPLETED':
      return 'PIX pago';
    case 'PENDING':
      return 'PIX pendente';
    case 'PROCESSING':
      return 'PIX enviando';
    case 'FAILED':
      return 'PIX falhou';
    case 'CANCELED':
      return 'PIX cancelado';
    case 'SKIPPED':
      return 'Sem PIX';
    default:
      return '—';
  }
}

function formatCpfInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  }
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function formatPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function normalizePixKey(type: InfluencerPixKeyType, raw: string): string {
  if (type === 'EMAIL') return raw.trim().toLowerCase();
  return raw.replace(/\D/g, '');
}

function validatePixKey(type: InfluencerPixKeyType, raw: string): string | null {
  const value = raw.trim();
  if (!value) return 'Informe a chave PIX.';

  if (type === 'CPF') {
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 11) return 'Chave PIX (CPF) deve ter 11 dígitos.';
    return null;
  }

  if (type === 'PHONE') {
    const digits = value.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 11) {
      return 'Chave PIX (telefone) deve ter 10 ou 11 dígitos (com DDD).';
    }
    return null;
  }

  const email = value.toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Chave PIX (e-mail) inválida.';
  }
  return null;
}

function formatPixKeyLabel(type: InfluencerPixKeyType | '', key: string): string {
  if (!type || !key) return '—';
  if (type === 'CPF') {
    const d = key.replace(/\D/g, '');
    if (d.length !== 11) return key;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }
  if (type === 'PHONE') {
    const d = key.replace(/\D/g, '');
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return key;
  }
  return key;
}

function pixKeyTypeLabel(type: InfluencerPixKeyType | ''): string {
  return INFLUENCER_PIX_KEY_TYPES.find((t) => t.value === type)?.label ?? '—';
}

export type InfluencerFormData = {
  name: string;
  cpf: string;
  email: string;
  phone: string;
  instagram: string;
  pixKeyType: InfluencerPixKeyType;
  pixKey: string;
  commissionPercent: number;
};

const emptyInfluencerForm = (): InfluencerFormData => ({
  name: '',
  cpf: '',
  email: '',
  phone: '',
  instagram: '',
  pixKeyType: 'CPF',
  pixKey: '',
  commissionPercent: 10,
});

function recordToFormData(inf: InfluencerRecord): InfluencerFormData {
  const pixKeyType = (inf.pixKeyType || 'CPF') as InfluencerPixKeyType;
  let pixKey = inf.pixKey || '';
  if (pixKeyType === 'CPF' && pixKey) pixKey = formatCpfInput(pixKey);
  if (pixKeyType === 'PHONE' && pixKey) pixKey = formatPhoneInput(pixKey);

  return {
    name: inf.name,
    cpf: inf.cpf ? formatCpfInput(inf.cpf) : '',
    email: inf.email,
    phone: inf.phone ? formatPhoneInput(inf.phone) : '',
    instagram: inf.instagram,
    pixKeyType,
    pixKey,
    commissionPercent:
      typeof inf.commissionPercent === 'number' && inf.commissionPercent > 0
        ? inf.commissionPercent
        : 10,
  };
}

function validateInfluencerForm(data: InfluencerFormData): string | null {
  if (!data.name.trim() || !data.cpf.trim() || !data.email.trim()) {
    return 'Nome, CPF e e-mail são obrigatórios.';
  }
  if (!Number.isFinite(data.commissionPercent) || data.commissionPercent <= 0 || data.commissionPercent > 100) {
    return 'A comissão deve ser um percentual entre 0,01 e 100.';
  }
  return validatePixKey(data.pixKeyType, data.pixKey);
}

type InfluencerFormFieldsProps = {
  form: InfluencerFormData;
  onChange: (updater: (prev: InfluencerFormData) => InfluencerFormData) => void;
  disabled?: boolean;
  idPrefix?: string;
};

const InfluencerFormFields: React.FC<InfluencerFormFieldsProps> = ({
  form,
  onChange,
  disabled = false,
  idPrefix = 'inf',
}) => (
  <div className="grid sm:grid-cols-2 gap-4">
    <div>
      <label htmlFor={`${idPrefix}-name`} className="block text-sm font-medium text-gray-700 mb-1">
        Nome *
      </label>
      <input
        id={`${idPrefix}-name`}
        className="w-full border border-gray-300 rounded-lg px-3 py-2"
        value={form.name}
        onChange={(e) => onChange((f) => ({ ...f, name: e.target.value }))}
        disabled={disabled}
      />
    </div>
    <div>
      <label htmlFor={`${idPrefix}-cpf`} className="block text-sm font-medium text-gray-700 mb-1">
        CPF *
      </label>
      <input
        id={`${idPrefix}-cpf`}
        className="w-full border border-gray-300 rounded-lg px-3 py-2"
        value={form.cpf}
        onChange={(e) => onChange((f) => ({ ...f, cpf: formatCpfInput(e.target.value) }))}
        disabled={disabled}
      />
    </div>
    <div>
      <label htmlFor={`${idPrefix}-email`} className="block text-sm font-medium text-gray-700 mb-1">
        E-mail *
      </label>
      <input
        id={`${idPrefix}-email`}
        type="email"
        className="w-full border border-gray-300 rounded-lg px-3 py-2"
        value={form.email}
        onChange={(e) => onChange((f) => ({ ...f, email: e.target.value }))}
        disabled={disabled}
      />
    </div>
    <div>
      <label htmlFor={`${idPrefix}-phone`} className="block text-sm font-medium text-gray-700 mb-1">
        Celular
      </label>
      <input
        id={`${idPrefix}-phone`}
        className="w-full border border-gray-300 rounded-lg px-3 py-2"
        value={form.phone}
        onChange={(e) => onChange((f) => ({ ...f, phone: formatPhoneInput(e.target.value) }))}
        disabled={disabled}
      />
    </div>
    <div className="sm:col-span-2">
      <label htmlFor={`${idPrefix}-instagram`} className="block text-sm font-medium text-gray-700 mb-1">
        Instagram
      </label>
      <input
        id={`${idPrefix}-instagram`}
        className="w-full border border-gray-300 rounded-lg px-3 py-2"
        placeholder="@usuario"
        value={form.instagram}
        onChange={(e) => onChange((f) => ({ ...f, instagram: e.target.value }))}
        disabled={disabled}
      />
    </div>
    <div>
      <label htmlFor={`${idPrefix}-pix-type`} className="block text-sm font-medium text-gray-700 mb-1">
        Tipo de chave PIX *
      </label>
      <select
        id={`${idPrefix}-pix-type`}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
        value={form.pixKeyType}
        onChange={(e) =>
          onChange((f) => ({
            ...f,
            pixKeyType: e.target.value as InfluencerPixKeyType,
            pixKey: '',
          }))
        }
        disabled={disabled}
      >
        {INFLUENCER_PIX_KEY_TYPES.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
    <div>
      <label htmlFor={`${idPrefix}-pix-key`} className="block text-sm font-medium text-gray-700 mb-1">
        Chave PIX *
      </label>
      <input
        id={`${idPrefix}-pix-key`}
        type={form.pixKeyType === 'EMAIL' ? 'email' : 'text'}
        className="w-full border border-gray-300 rounded-lg px-3 py-2"
        placeholder={
          form.pixKeyType === 'CPF'
            ? '000.000.000-00'
            : form.pixKeyType === 'PHONE'
              ? '(00) 00000-0000'
              : 'email@exemplo.com'
        }
        value={form.pixKey}
        onChange={(e) => {
          const raw = e.target.value;
          let pixKey = raw;
          if (form.pixKeyType === 'CPF') pixKey = formatCpfInput(raw);
          if (form.pixKeyType === 'PHONE') pixKey = formatPhoneInput(raw);
          onChange((f) => ({ ...f, pixKey }));
        }}
        disabled={disabled}
      />
    </div>
    <div>
      <label htmlFor={`${idPrefix}-commission`} className="block text-sm font-medium text-gray-700 mb-1">
        Comissão (%) *
      </label>
      <input
        id={`${idPrefix}-commission`}
        type="number"
        min={0.01}
        max={100}
        step={0.01}
        className="w-full border border-gray-300 rounded-lg px-3 py-2"
        value={form.commissionPercent}
        onChange={(e) =>
          onChange((f) => ({ ...f, commissionPercent: Number(e.target.value) }))
        }
        disabled={disabled}
      />
      <p className="text-xs text-gray-500 mt-1">
        Percentual sobre cada venda e renovação. O PIX sai automaticamente quando o pagamento cair.
      </p>
    </div>
  </div>
);

const InfluencerAdminPanel: React.FC = () => {
  const [panelTab, setPanelTab] = useState<'cadastro' | 'vendas'>('cadastro');
  const [influencers, setInfluencers] = useState<InfluencerRecord[]>([]);
  const [sales, setSales] = useState<InfluencerSaleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [salesFilterInfluencerId, setSalesFilterInfluencerId] = useState<string>('');
  const [reprocessingSaleId, setReprocessingSaleId] = useState<string | null>(null);

  const [form, setForm] = useState<InfluencerFormData>(emptyInfluencerForm);
  const [editingInfluencer, setEditingInfluencer] = useState<InfluencerRecord | null>(null);
  const [editForm, setEditForm] = useState<InfluencerFormData>(emptyInfluencerForm);
  const [editModalError, setEditModalError] = useState<string | null>(null);

  const loadInfluencers = useCallback(async () => {
    const snap = await getDocs(query(collection(db, 'influencers'), orderBy('createdAt', 'desc')));
    const list: InfluencerRecord[] = snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        name: data.name || '',
        cpf: data.cpf || '',
        email: data.email || '',
        phone: data.phone || '',
        instagram: data.instagram || '',
        pixKeyType: (data.pixKeyType as InfluencerPixKeyType) || '',
        pixKey: data.pixKey || '',
        commissionPercent: Number(data.commissionPercent) > 0 ? Number(data.commissionPercent) : 10,
        code: data.code || '',
        active: data.active !== false,
        createdAt: data.createdAt?.toDate?.(),
      };
    });
    setInfluencers(list);
    return list;
  }, []);

  const loadSales = useCallback(async (influencerId?: string) => {
    const snap = await getDocs(query(collection(db, 'influencerSales'), orderBy('createdAt', 'desc')));
    let rows = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          influencerId: data.influencerId || '',
          influencerCode: data.influencerCode || '',
          influencerName: data.influencerName || '',
          buyerName: data.buyerName || '—',
          buyerEmail: data.buyerEmail || '—',
          planName: data.planName || '—',
          value: Number(data.value) || 0,
          paymentType: data.paymentType || 'one_time',
          status: data.status || '',
          commissionPercent: Number(data.commissionPercent) || 0,
          commissionValue: Number(data.commissionValue) || 0,
          payoutStatus: data.payoutStatus || '',
          payoutError: data.payoutError || '',
          createdAt: data.createdAt?.toDate?.(),
        };
      });
    if (influencerId) {
      rows = rows.filter((r) => r.influencerId === influencerId);
    }
    setSales(rows);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        await loadInfluencers();
        await loadSales();
      } catch (e) {
        console.error(e);
        setError('Não foi possível carregar influencers.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [loadInfluencers, loadSales]);

  useEffect(() => {
    if (panelTab !== 'vendas') return;
    loadSales(salesFilterInfluencerId || undefined).catch((e) => {
      console.error(e);
      setError('Não foi possível filtrar vendas.');
    });
  }, [salesFilterInfluencerId, panelTab, loadSales]);

  const handleCreate = async () => {
    setError(null);
    setSuccess(null);
    const validationError = validateInfluencerForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    try {
      const code = generateInfluencerCode(form.name);
      const pixKey = normalizePixKey(form.pixKeyType, form.pixKey);
      await addDoc(collection(db, 'influencers'), {
        name: form.name.trim(),
        cpf: form.cpf.replace(/\D/g, ''),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.replace(/\D/g, ''),
        instagram: form.instagram.trim().replace(/^@/, ''),
        pixKeyType: form.pixKeyType,
        pixKey,
        commissionPercent: form.commissionPercent,
        code,
        active: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      setForm(emptyInfluencerForm());
      setSuccess('Influencer cadastrado. Link gerado automaticamente.');
      await loadInfluencers();
    } catch (e) {
      console.error(e);
      setError('Erro ao cadastrar influencer.');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (inf: InfluencerRecord) => {
    setEditModalError(null);
    setEditingInfluencer(inf);
    setEditForm(recordToFormData(inf));
  };

  const closeEditModal = () => {
    if (saving) return;
    setEditingInfluencer(null);
    setEditModalError(null);
    setEditForm(emptyInfluencerForm());
  };

  const handleSaveEdit = async () => {
    if (!editingInfluencer) return;

    setEditModalError(null);
    const validationError = validateInfluencerForm(editForm);
    if (validationError) {
      setEditModalError(validationError);
      return;
    }

    setSaving(true);
    try {
      const pixKey = normalizePixKey(editForm.pixKeyType, editForm.pixKey);
      await updateDoc(doc(db, 'influencers', editingInfluencer.id), {
        name: editForm.name.trim(),
        cpf: editForm.cpf.replace(/\D/g, ''),
        email: editForm.email.trim().toLowerCase(),
        phone: editForm.phone.replace(/\D/g, ''),
        instagram: editForm.instagram.trim().replace(/^@/, ''),
        pixKeyType: editForm.pixKeyType,
        pixKey,
        commissionPercent: editForm.commissionPercent,
        updatedAt: Timestamp.now(),
      });
      setSuccess(`Dados de ${editForm.name.trim()} atualizados. O link de rastreio permanece o mesmo.`);
      setEditingInfluencer(null);
      setEditModalError(null);
      setEditForm(emptyInfluencerForm());
      await loadInfluencers();
    } catch (e) {
      console.error(e);
      setEditModalError('Erro ao salvar alterações.');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async (inf: InfluencerRecord) => {
    const url = buildInfluencerTrackingUrl(inf.code);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(inf.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setError('Não foi possível copiar o link.');
    }
  };

  const toggleActive = async (inf: InfluencerRecord) => {
    try {
      await updateDoc(doc(db, 'influencers', inf.id), {
        active: !inf.active,
        updatedAt: Timestamp.now(),
      });
      await loadInfluencers();
    } catch (e) {
      console.error(e);
      setError('Erro ao atualizar status.');
    }
  };

  const reprocessPayout = async (saleId: string) => {
    setError(null);
    setSuccess(null);
    setReprocessingSaleId(saleId);
    try {
      const fn = httpsCallable<{ saleId: string }, { success: boolean; message: string }>(
        functions,
        'reprocessInfluencerPayout',
      );
      const result = await fn({ saleId });
      if (result.data.success) {
        setSuccess(result.data.message);
      } else {
        setError(result.data.message);
      }
      await loadSales(salesFilterInfluencerId || undefined);
    } catch (e: unknown) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Erro ao reprocessar PIX do afiliado.');
    } finally {
      setReprocessingSaleId(null);
    }
  };

  return (
    <div className="p-3 sm:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Influencers
        </h2>
        <nav className="flex gap-2 rounded-lg bg-gray-100 p-1 w-fit">
          <button
            type="button"
            onClick={() => setPanelTab('cadastro')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              panelTab === 'cadastro' ? 'bg-white shadow text-blue-600' : 'text-gray-600'
            }`}
          >
            Cadastro e links
          </button>
          <button
            type="button"
            onClick={() => setPanelTab('vendas')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              panelTab === 'vendas' ? 'bg-white shadow text-blue-600' : 'text-gray-600'
            }`}
          >
            Controle de vendas
          </button>
        </nav>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
          <span className="text-sm text-red-800">{error}</span>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex gap-2">
          <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
          <span className="text-sm text-green-800">{success}</span>
        </div>
      )}

      {loading ? (
        <p className="text-gray-500 text-center py-8">Carregando...</p>
      ) : panelTab === 'cadastro' ? (
        <>
          <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
            <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Novo influencer
            </h3>
            <InfluencerFormFields form={form} onChange={setForm} disabled={saving} idPrefix="new" />
            <p className="text-xs text-gray-500 mt-3">
              Após salvar, o link será{' '}
              <code className="bg-gray-100 px-1 rounded">constelabrasil.com.br/constelador?ref=codigo</code>.
              O cookie vale 180 dias (último clique).
              Contas marcadas como indicação dele continuam atribuídas nas compras seguintes.
            </p>
            <button
              type="button"
              onClick={handleCreate}
              disabled={saving}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Cadastrar e gerar link'}
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <h3 className="text-base font-semibold text-gray-800 p-4 border-b flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Influencers cadastrados ({influencers.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-4 py-3">Nome</th>
                    <th className="px-4 py-3">Instagram</th>
                    <th className="px-4 py-3">CPF</th>
                    <th className="px-4 py-3">PIX</th>
                    <th className="px-4 py-3">Comissão</th>
                    <th className="px-4 py-3">Link</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {influencers.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        Nenhum influencer cadastrado.
                      </td>
                    </tr>
                  ) : (
                    influencers.map((inf) => (
                      <tr key={inf.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{inf.name}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {inf.instagram ? `@${inf.instagram.replace(/^@/, '')}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{maskCpf(inf.cpf)}</td>
                        <td className="px-4 py-3 text-gray-600">
                          <div className="text-xs font-medium text-gray-500">{pixKeyTypeLabel(inf.pixKeyType)}</div>
                          <div className="text-sm">{formatPixKeyLabel(inf.pixKeyType, inf.pixKey)}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{inf.commissionPercent}%</td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded break-all">
                            ?ref={inf.code}
                          </code>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              inf.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {inf.active ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-4 py-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => openEditModal(inf)}
                            className="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900 text-xs font-medium"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => copyLink(inf)}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium"
                          >
                            {copiedId === inf.id ? (
                              <>
                                <CheckCircle className="w-3.5 h-3.5" /> Copiado
                              </>
                            ) : (
                              <>
                                <Copy className="w-3.5 h-3.5" /> Copiar link
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleActive(inf)}
                            className="text-xs text-gray-600 hover:text-gray-900 underline"
                          >
                            {inf.active ? 'Desativar' : 'Ativar'}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div className="flex-1 w-full sm:max-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-1">Filtrar por influencer</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                value={salesFilterInfluencerId}
                onChange={(e) => setSalesFilterInfluencerId(e.target.value)}
              >
                <option value="">Todos</option>
                {influencers.map((inf) => (
                  <option key={inf.id} value={inf.id}>
                    {inf.name} ({inf.code})
                  </option>
                ))}
              </select>
            </div>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <BarChart3 className="w-4 h-4" />
              {sales.length} venda(s), incluindo 1ª mensalidade e renovações
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-4 py-3">Data</th>
                    <th className="px-4 py-3">Influencer</th>
                    <th className="px-4 py-3">Plano</th>
                    <th className="px-4 py-3">Valor</th>
                    <th className="px-4 py-3">Comissão</th>
                    <th className="px-4 py-3">Comprador</th>
                    <th className="px-4 py-3">Tipo</th>
                    <th className="px-4 py-3">PIX</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sales.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                        Nenhuma venda atribuída ainda.
                      </td>
                    </tr>
                  ) : (
                    sales.map((sale) => (
                      <tr key={sale.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {sale.createdAt
                            ? sale.createdAt.toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{sale.influencerName}</div>
                          <div className="text-xs text-gray-500">{sale.influencerCode}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-800">{sale.planName}</td>
                        <td className="px-4 py-3 font-medium">{formatCurrency(sale.value)}</td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {sale.commissionValue
                            ? `${formatCurrency(sale.commissionValue)} (${sale.commissionPercent || 10}%)`
                            : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div>{sale.buyerName}</div>
                          <div className="text-xs text-gray-500">{sale.buyerEmail}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {paymentTypeLabel(sale.paymentType)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-800">{payoutStatusLabel(sale.payoutStatus || '')}</div>
                          {sale.payoutError ? (
                            <div className="text-xs text-red-600 max-w-[180px]">{sale.payoutError}</div>
                          ) : null}
                          {sale.payoutStatus === 'FAILED' && (
                            <button
                              type="button"
                              className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                              onClick={() => reprocessPayout(sale.id)}
                              disabled={reprocessingSaleId === sale.id}
                            >
                              {reprocessingSaleId === sale.id ? 'Reprocessando...' : 'Reprocessar PIX'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {editingInfluencer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-influencer-title"
          onClick={closeEditModal}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b px-5 py-4 sticky top-0 bg-white z-10">
              <div>
                <h3 id="edit-influencer-title" className="text-lg font-semibold text-gray-900">
                  Editar influencer
                </h3>
                <p className="text-sm text-gray-500 mt-1">{editingInfluencer.name}</p>
              </div>
              <button
                type="button"
                onClick={closeEditModal}
                className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                aria-label="Fechar"
                disabled={saving}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-5 py-5 space-y-4">
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                <p className="font-medium mb-1">Link de rastreio (não será alterado)</p>
                <code className="text-xs bg-white/80 px-2 py-1 rounded break-all">
                  {buildInfluencerTrackingUrl(editingInfluencer.code)}
                </code>
                <p className="text-xs text-blue-800 mt-2">
                  Este endereço não muda ao editar nome ou PIX.
                </p>
              </div>

              {editModalError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                  <span className="text-sm text-red-800">{editModalError}</span>
                </div>
              )}

              <InfluencerFormFields
                form={editForm}
                onChange={setEditForm}
                disabled={saving}
                idPrefix="edit"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t px-5 py-4 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={closeEditModal}
                disabled={saving}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Salvar alterações'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfluencerAdminPanel;
